import RecruitingInterviewResults from '../../components/RecruitingInterviewResults';
import MultipleChoiceExamResults from '../../components/MultipleChoiceExamResults';
import { camelCaseToSpacedString } from '../../../../../utils/generalHelpers';
import { getExaminersThunk } from '../../../../../store/workspace/thunks';
import { setExamStatsTab } from '../../../../../store/workspace/slice';
import { useDispatch, useSelector } from '../../../../../store/store';
import { ExamStatsTab } from '../../../../../store/workspace/types';
import CodingExamResults from '../../components/CodingExamResults';
import { useNavigate, useParams } from 'react-router-dom';
import DropDown from '../../../../../components/DropDown';
import styles from './style.module.scss';
import { useEffect } from 'react';
import {
    selectExamStatsTab,
    selectCodingExamStats,
    selectExamStatsJobInfo,
    selectMultipleChoiceExamStats,
    selectRecruitingInterviewStatists,
} from '../../../../../store/workspace/selectors';

export default function ExamResults() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { jobId, examinerId } = useParams();
    const tab = useSelector(selectExamStatsTab);
    const jobInfo = useSelector(selectExamStatsJobInfo);
    const codingExam = useSelector(selectCodingExamStats);
    const multipleChoiceExam = useSelector(selectMultipleChoiceExamStats);
    const recruitingInterview = useSelector(selectRecruitingInterviewStatists);

    useEffect(() => {
        if (!tab ||
            (tab === 'coding' && !codingExam) ||
            (tab === 'recruitingInterview' && !recruitingInterview) ||
            (tab === 'multipleChoice' && !multipleChoiceExam)) {
            fetchTablesData(tab);
        }
    }, [tab]);

    const fetchTablesData = (tab: ExamStatsTab) => {
        if (!jobId) return;
        dispatch(getExaminersThunk({ tab, jobId }))
    };

    const changeTab = (status: 'pending' | 'enable' | 'disable', tab: ExamStatsTab) => {
        if (status !== 'enable') return;
        dispatch(setExamStatsTab(tab));
    };

    return (
        <div className={styles.container}>
            <span
                className={styles.backToList}
                onClick={() => navigate(-1)}
            >
                Back to exam statistics
            </span>

            <div className={styles.main}>
                <div className={styles.main_header}>
                    <span className={styles.main_header_title}>
                        Exam sheet
                    </span>
                </div>

                <div className={styles.main_tab}>
                    {jobInfo?.recruitingInterview === 'enable' && <div
                        className={styles.main_tab_item}
                        data-is-active={tab === 'recruitingInterview'}
                        onClick={() => changeTab(jobInfo?.recruitingInterview, 'recruitingInterview')}
                    >
                        <span>Recruiting interview</span>
                    </div>}
                    {(jobInfo?.multipleChoiceExam === 'enable' ||
                        jobInfo?.multipleChoiceExam === 'pending') && <div
                            className={styles.main_tab_item}
                            data-is-active={tab === 'multipleChoice'}
                            onClick={() => changeTab(jobInfo?.multipleChoiceExam, 'multipleChoice')}
                            data-is-disable={jobInfo?.multipleChoiceExam === 'pending'}
                        >
                            <span>Multiple choice exam</span>
                        </div>}
                    {(jobInfo?.codingExam === 'enable' ||
                        jobInfo?.codingExam === 'pending') && <div
                            className={styles.main_tab_item}
                            data-is-active={tab === 'coding'}
                            onClick={() => changeTab(jobInfo?.codingExam, 'coding')}
                            data-is-disable={jobInfo?.codingExam === 'pending'}
                        >
                            <span>Coding exam</span>
                        </div>}
                </div>

                <DropDown
                    idKey='id'
                    titleKey='title'
                    containerClassName={styles.main_tab_dropdown}
                    selectedItem={{ id: tab, title: camelCaseToSpacedString(tab || '') }}
                    setItem={(item: any) => changeTab('enable', item.id as ExamStatsTab)}
                    dropDownList={[
                        { id: 'recruitingInterview', title: 'Recruiting interview' },
                        { id: 'multipleChoice', title: 'Multiple choice exam' },
                        { id: 'coding', title: 'Coding exam' },
                    ]}
                />

                <div className={styles.main_table}>
                    {tab === 'recruitingInterview' &&
                        <RecruitingInterviewResults
                            applicantId={examinerId}
                            interviewData={recruitingInterview}
                        />}

                    {tab === 'multipleChoice' &&
                        <MultipleChoiceExamResults
                            examData={multipleChoiceExam}
                            examinerData={multipleChoiceExam?.examiners.find(i => i.userId === examinerId)}
                        />}

                    {tab === 'coding' &&
                        <CodingExamResults
                            examData={codingExam}
                            examinerData={codingExam?.examiners.find(i => i.userId === examinerId)}
                        />}
                </div>
            </div>
        </div>
    )
};
